import React from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { renderMarkdown } from "@impero-com/ui";
import { Typography } from "antd";

import { MarkdownInput } from "components/form/fields/markdownInput";
import { MarkdownEditorBlurEvent } from "components/markdownEditor/types";
import { MultiInputHelper } from "utils/multiInputHelper";

import { BaseFormDisplay } from "./formDisplay";

import { BaseFormField } from ".";

const helper: MultiInputHelper<string, React.ReactNode> = new MultiInputHelper({
    isEqual: (a: string, b: string) => a === b,
    isBlank: (val: string) => /^\s*$/.test(val),
    display: (value: string) => renderMarkdown(value),
});

type MultiTextAreaProps =
    | ({ type: "CREATE" } & WrappedWriteProps)
    | ({ type: "EDIT" } & WrappedWriteProps)
    | ({ type: "READ_ONLY" } & WrappedReadProps);

type Wrapped<T> = T & { label: string };

interface ReadProps {
    name: string;
    values?: string[];
}

type WrappedReadProps = Wrapped<ReadProps>;

interface WriteProps extends ReadProps {
    onChange: (v: string) => void;
}

type WrappedWriteProps = Wrapped<WriteProps>;

class MultiTextArea extends React.PureComponent<MultiTextAreaProps> {
    render() {
        const { props } = this;
        switch (props.type) {
            case "READ_ONLY": {
                const { label, name, values } = props;
                return (
                    <WrappedReadOnlyTextArea
                        label={label}
                        name={name}
                        values={values}
                    />
                );
            }
            case "EDIT": {
                const { label, name, values, onChange } = props;
                return (
                    <WrappedEditTextArea
                        label={label}
                        name={name}
                        values={values}
                        onChange={onChange}
                    />
                );
            }
            case "CREATE": {
                const { label, name, values, onChange } = props;
                return (
                    <WrappedCreateTextArea
                        label={label}
                        name={name}
                        values={values}
                        onChange={onChange}
                    />
                );
            }
            default:
                return assertNever(props);
        }
    }
}

type RawReadOnlyTextAreaProps = ReadProps;

class RawReadOnlyTextArea extends React.PureComponent<RawReadOnlyTextAreaProps> {
    render() {
        const { value, type } = helper.deriveReadOnlyValueAndType(
            this.props.values
        );
        return <Typography.Text type={type}>{value}</Typography.Text>;
    }
}

interface RawEditableTextAreaProps extends WriteProps {
    autoFocus?: boolean;
    onFocus?: () => void;
    onBlur?: (
        e: React.FocusEvent<HTMLTextAreaElement> | MarkdownEditorBlurEvent
    ) => void;
}

interface RawEditableTextAreaState {
    derivedValue: string;
    trackedValue: string;
    placeholder: string;
}

class RawEditableTextArea extends React.Component<
    RawEditableTextAreaProps,
    RawEditableTextAreaState
> {
    state: RawEditableTextAreaState = {
        derivedValue: "",
        trackedValue: "",
        placeholder: "",
    };

    static getDerivedStateFromProps(
        props: RawEditableTextAreaProps,
        state: RawEditableTextAreaState
    ) {
        const { value, placeholder } = helper.deriveValueAndPlaceholder(
            props.values
        );
        if (value !== state.derivedValue) {
            return {
                derivedValue: value,
                trackedValue: value,
                placeholder,
            };
        }
        return null;
    }

    render() {
        const { props, state } = this;
        const { autoFocus, onFocus } = props;
        const { trackedValue, placeholder } = state;

        return (
            <MarkdownInput
                placeholder={placeholder}
                autoFocus={autoFocus}
                value={trackedValue}
                onChange={this.onMarkdownEditorChange}
                onFocus={onFocus}
                onBlur={this.onBlur}
            />
        );
    }

    onTextAreaChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ): void => {
        this.setState({
            trackedValue: event.target.value,
        });
    };

    onMarkdownEditorChange = (newContent: string): void => {
        this.setState({
            trackedValue: newContent,
        });
    };

    onBlur = (
        event: React.FocusEvent<HTMLTextAreaElement> | MarkdownEditorBlurEvent
    ): void => {
        this.props.onBlur?.(event);
        if (this.didChange()) {
            this.props.onChange?.(this.state.trackedValue);
        }
    };

    private didChange() {
        return this.state.derivedValue !== this.state.trackedValue;
    }
}

class WrappedReadOnlyTextArea extends React.PureComponent<WrappedReadProps> {
    render() {
        const { label, name, values } = this.props;
        return (
            <BaseFormDisplay label={label}>
                <RawReadOnlyTextArea name={name} values={values} />
            </BaseFormDisplay>
        );
    }
}

interface WrappedEditTextAreaState {
    isEditing: boolean;
}

class WrappedEditTextArea extends React.Component<
    WrappedWriteProps,
    WrappedEditTextAreaState
> {
    state = {
        isEditing: false,
    };

    render() {
        const { label, name, values, onChange } = this.props;
        const { isEditing } = this.state;

        return (
            <BaseFormField
                getComponent={() => (
                    <RawEditableTextArea
                        name={name}
                        autoFocus={true}
                        values={values}
                        onChange={onChange}
                        onBlur={this.onBlur}
                    />
                )}
                getReadonlyComponent={() => (
                    <RawReadOnlyTextArea name={name} values={values} />
                )}
                onClick={this.onClick}
                hasFeedback={false}
                label={label}
                readonly={!isEditing}
            />
        );
    }

    onBlur = (
        _: React.FocusEvent<HTMLTextAreaElement> | MarkdownEditorBlurEvent
    ) => {
        this.setState({
            isEditing: false,
        });
    };

    onClick = () => {
        this.setState({
            isEditing: true,
        });
    };
}

class WrappedCreateTextArea extends React.PureComponent<WrappedWriteProps> {
    render() {
        const { label, name, values, onChange } = this.props;
        return (
            <BaseFormField
                getComponent={() => (
                    <RawEditableTextArea
                        name={name}
                        values={values}
                        onChange={onChange}
                    />
                )}
                hasFeedback={false}
                label={label}
            />
        );
    }
}

export { MultiTextArea, type MultiTextAreaProps };
